import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import "./FormStyles.css";
import "./StreamType.css";
import useAxios from "./useAxios";
import useSessionStorage from "./useSessionStorage";

const StreamType = ({ logout }) => {
  const { id } = useParams();
  const [streamType, setStreamType] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [axios] = useAxios(true, logout);
  const navigate = useNavigate();
  const [tenant] = useSessionStorage("tenant");
  const [authWindow, setAuthWindow] = useState(null);

  useEffect(() => {
    if (id === "new") {
      setStreamType({
        name: "",
        tenant: tenant.super ? null : tenant,
        service: 1,
        apiKey: "",
        clientId: "",
        clientSecret: "",
        obsUrl: "",
        obsSecret: "",
        isPublic: false,
      });
    } else {
      axios.get("streamtype/" + id, setStreamType);
    }
    if (tenant.super) {
      axios.get("tenants", setTenants);
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStreamType((prevStreamType) => ({
      ...prevStreamType,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTenantChange = (e) => {
    const { value } = e.target;
    const selectedTenant = tenants.find((t) => t.id === value);
    setStreamType((prev) => ({ ...prev, tenant: selectedTenant }));
  };

  const onSave = () => {
    axios.post("streamtype/save/", streamType, onCancel);
  };

  const onCancel = () => {
    navigate("/configuration/streamtypes");
  };

  const handleAuthorizeYouTube = () => {
    if (id === "new" || !streamType.id) {
      alert("Please save the Stream Type first before authorizing YouTube.");
      return;
    }

    axios.get(`streamtype/${streamType.id}/oauth-url`, (data) => {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.authUrl,
        "YouTube Authorization",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      setAuthWindow(popup);

      // Poll to check if authorization is complete
      const checkInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkInterval);
          setAuthWindow(null);
          // Reload stream type to get updated refresh token status
          axios.get("streamtype/" + id, setStreamType);
        }
      }, 1000);
    });
  };

  if (streamType === null || (tenant.super && tenants.length === 0)) {
    return <h1>Loading</h1>;
  }

  return (
    <div className="edit-stream-type-form">
      <h2>{id === "new" ? "Create Stream Type" : "Edit Stream Type"}</h2>
      <form className="streamtype-form-fields">
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            name="name"
            id="name"
            value={streamType.name}
            onChange={handleChange}
            required
            className="form-control"
          />
        </div>
        {tenant.super && (
          <div className="form-group">
            <label htmlFor="tenant">Tenant:</label>
            <select
              name="tenant"
              id="tenant"
              value={streamType.tenant?.id || ""}
              onChange={handleTenantChange}
              required
              className="form-control"
            >
              <option value="">Select a tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-group">
          <label htmlFor="apiKey">API Key:</label>
          <input
            type="text"
            name="apiKey"
            id="apiKey"
            value={streamType.apiKey}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="clientId">Client ID:</label>
          <input
            type="text"
            name="clientId"
            id="clientId"
            value={streamType.clientId}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="clientSecret">Client Secret:</label>
          <input
            type="password"
            name="clientSecret"
            id="clientSecret"
            value={streamType.clientSecret}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="obsUrl">OBS URL:</label>
          <input
            type="text"
            name="obsUrl"
            id="obsUrl"
            value={streamType.obsUrl}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="obsSecret">OBS Secret:</label>
          <input
            type="password"
            name="obsSecret"
            id="obsSecret"
            value={streamType.obsSecret}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="isPublic">Is Public:</label>
          <input
            type="checkbox"
            name="isPublic"
            id="isPublic"
            checked={streamType.isPublic || false}
            onChange={handleChange}
            className="form-check-input"
          />
        </div>
        {id !== "new" && (
          <div className="form-group">
            <label>YouTube Authorization:</label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {streamType.hasRefreshToken ? (
                <span style={{ color: "green", fontWeight: "bold" }}>
                  ✓ Authorized
                </span>
              ) : (
                <span style={{ color: "orange", fontWeight: "bold" }}>
                  ⚠ Not Authorized
                </span>
              )}
              <button
                type="button"
                onClick={handleAuthorizeYouTube}
                className="submit-button"
                style={{ marginLeft: "10px" }}
              >
                {streamType.hasRefreshToken ? "Re-authorize" : "Authorize YouTube"}
              </button>
            </div>
            <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
              One-time authorization to allow automated streaming. This captures a refresh token for headless operation.
            </small>
          </div>
        )}
        <div className="form-buttons">
          <button type="button" onClick={onSave} className="submit-button">
            Save
          </button>
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default StreamType;
